/**
 * Toast Notification Context
 * Tüm sayfalarda tutarlı bildirim gösterimi sağlar
 * 
 * Kullanım:
 * const { showSuccess, showError, showWarning, showConfirm } = useToast();
 * showSuccess('İşlem başarılı!');
 * showError('Bir hata oluştu.');
 * 
 * Confirm dialog:
 * const confirmed = await showConfirm({ message: 'Silmek istediğinize emin misiniz?' });
 */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import './Toast.css';

const ToastContext = createContext(null);

let toastId = 0;

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const timersRef = useRef({});

    const removeToast = useCallback((id) => {
        if (timersRef.current[id]) {
            clearTimeout(timersRef.current[id]);
            delete timersRef.current[id];
        }
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const showToast = useCallback((options) => {
        const id = ++toastId;
        const toast = {
            id,
            type: options.type || 'info',
            title: options.title,
            message: options.message,
            duration: options.duration ?? 5000,
            icon: options.icon,
            action: options.action
        };

        setToasts(prev => [...prev, toast]);

        // Auto-dismiss
        if (toast.duration > 0) {
            timersRef.current[id] = setTimeout(() => {
                removeToast(id);
            }, toast.duration);
        }

        return id;
    }, [removeToast]);

    // Legacy support - addToast
    const addToast = useCallback((message, type = 'info') => {
        return showToast({ message, type });
    }, [showToast]);

    // Shorthand methods
    const showSuccess = useCallback((message, title = 'Başarılı') => {
        return showToast({
            type: 'success',
            title,
            message,
            icon: 'fas fa-check-circle'
        });
    }, [showToast]);

    const showError = useCallback((message, title = 'Hata') => {
        return showToast({
            type: 'error',
            title,
            message,
            icon: 'fas fa-times-circle',
            duration: 8000 // Hatalar daha uzun süre görünsün
        });
    }, [showToast]);

    const showWarning = useCallback((message, title = 'Uyarı') => {
        return showToast({
            type: 'warning',
            title,
            message,
            icon: 'fas fa-exclamation-triangle'
        });
    }, [showToast]);

    const showInfo = useCallback((message, title = 'Bilgi') => {
        return showToast({
            type: 'info',
            title,
            message,
            icon: 'fas fa-info-circle'
        });
    }, [showToast]);

    // Confirmation dialog (alert/confirm yerine)
    const showConfirm = useCallback((options) => {
        return new Promise((resolve) => {
            const id = ++toastId;
            const toast = {
                id,
                type: 'confirm',
                title: options.title || 'Onay',
                message: options.message,
                icon: options.icon || 'fas fa-question-circle',
                confirmText: options.confirmText || 'Evet',
                cancelText: options.cancelText || 'İptal',
                destructive: options.destructive || false,
                onConfirm: () => {
                    removeToast(id);
                    resolve(true);
                },
                onCancel: () => {
                    removeToast(id);
                    resolve(false);
                },
                duration: 0 // Manuel kapatma gerekli
            };
            setToasts(prev => [...prev, toast]);
        });
    }, [removeToast]);

    const getIcon = (toast) => {
        if (toast.icon) return toast.icon;
        switch (toast.type) {
            case 'success': return 'fas fa-check-circle';
            case 'error': return 'fas fa-times-circle';
            case 'warning': return 'fas fa-exclamation-triangle';
            case 'confirm': return 'fas fa-question-circle';
            default: return 'fas fa-info-circle';
        }
    };

    const value = {
        addToast, // Legacy support
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showConfirm,
        removeToast
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            {toasts.length > 0 && (
                <div className="toast-container" role="region" aria-label="Bildirimler" aria-live="polite">
                    {toasts.map(toast => (
                        <div
                            key={toast.id}
                            className={`toast toast-${toast.type}`}
                            role={toast.type === 'error' ? 'alert' : 'status'}
                        >
                            <div className="toast-icon">
                                <i className={getIcon(toast)}></i>
                            </div>
                            <div className="toast-content">
                                {toast.title && <h4 className="toast-title">{toast.title}</h4>}
                                <p className="toast-message">{toast.message}</p>

                                {/* Confirm dialog buttons */}
                                {toast.type === 'confirm' && (
                                    <div className="toast-actions">
                                        <button
                                            className="toast-btn toast-btn-cancel"
                                            onClick={toast.onCancel}
                                        >
                                            {toast.cancelText}
                                        </button>
                                        <button
                                            className={`toast-btn toast-btn-confirm ${toast.destructive ? 'destructive' : ''}`}
                                            onClick={toast.onConfirm}
                                        >
                                            {toast.confirmText}
                                        </button>
                                    </div>
                                )}

                                {/* Custom action button */}
                                {toast.action && (
                                    <button
                                        className="toast-action-btn"
                                        onClick={() => {
                                            toast.action.onClick();
                                            removeToast(toast.id);
                                        }}
                                    >
                                        {toast.action.label}
                                    </button>
                                )}
                            </div>

                            {toast.type !== 'confirm' && (
                                <button
                                    className="toast-dismiss"
                                    onClick={() => removeToast(toast.id)}
                                    aria-label="Bildirimi kapat"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            )}

                            {/* Progress bar for auto-dismiss */}
                            {toast.duration > 0 && toast.type !== 'confirm' && (
                                <div
                                    className="toast-progress"
                                    style={{ animationDuration: `${toast.duration}ms` }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export default ToastContext;